import { defineModule } from '@portal/kernel';

export const manifest = defineModule({
  key: 'requests',
  version: '0.1.0',
  kind: 'business',
  coreVersion: '^0.1.0',
  name: { vi: 'Đề xuất & phê duyệt', en: 'Requests & approvals' },
  description: {
    vi: 'Đề xuất theo biểu mẫu, duyệt theo cấp quản lý và vai trò, uỷ quyền, nhắc duyệt.',
    en: 'Form-based requests approved along the management line and by roles, with delegation and reminders.',
  },
  dependsOn: [
    'core-system',
    'core-audit',
    'core-identity',
    'core-organization',
    'core-access',
    'core-files',
    'core-notification',
  ],
  database: { schema: 'requests' },
  permissions: [
    {
      key: 'requests.request.create',
      name: { vi: 'Tạo đề xuất', en: 'Create requests' },
      defaultGranted: true,
    },
    {
      key: 'requests.request.view',
      name: { vi: 'Xem đề xuất của người khác', en: "View others' requests" },
      dataScopes: ['department', 'department_tree', 'tenant'],
    },
    {
      key: 'requests.type.manage',
      name: { vi: 'Quản lý loại đề xuất và luồng duyệt', en: 'Manage request types and flows' },
    },
    {
      key: 'requests.leave.manage',
      name: {
        vi: 'Quản lý nghỉ phép (quỹ phép, ngày lễ)',
        en: 'Manage leave (balances, holidays)',
      },
    },
    {
      key: 'requests.payment.process',
      name: {
        vi: 'Chi tiền đề nghị thanh toán / tạm ứng, theo dõi hoàn ứng',
        en: 'Pay payment and advance requests, track advances',
      },
    },
    {
      key: 'requests.purchase.process',
      name: {
        vi: 'Xử lý mua sắm (nhận việc, đã giao, đề nghị thanh toán)',
        en: 'Handle purchases (take, deliver, request payment)',
      },
    },
    {
      key: 'requests.approve.director',
      name: { vi: 'Duyệt với vai trò Giám đốc', en: 'Approve as director' },
    },
    {
      key: 'requests.approve.accounting',
      name: { vi: 'Duyệt với vai trò Kế toán', en: 'Approve as accounting' },
    },
    {
      key: 'requests.approve.hr',
      name: { vi: 'Duyệt với vai trò Nhân sự', en: 'Approve as HR' },
    },
    {
      key: 'requests.approve.office',
      name: { vi: 'Duyệt với vai trò Hành chính', en: 'Approve as office administration' },
    },
  ],
  navigation: [
    {
      id: 'requests',
      label: { vi: 'Đề xuất', en: 'Requests' },
      icon: 'file-check',
      order: 50,
      children: [
        {
          id: 'requests.mine',
          label: { vi: 'Đề xuất của tôi', en: 'My requests' },
          icon: 'file-text',
          path: '/requests',
          permission: 'requests.request.create',
          order: 1,
        },
        {
          id: 'requests.inbox',
          label: { vi: 'Chờ tôi duyệt', en: 'Awaiting my approval' },
          icon: 'inbox',
          path: '/requests/inbox',
          permission: 'requests.request.create',
          order: 2,
        },
        {
          id: 'requests.all',
          label: { vi: 'Tất cả đề xuất', en: 'All requests' },
          icon: 'clipboard-list',
          path: '/requests/all',
          permission: 'requests.request.view',
          order: 3,
        },
        {
          id: 'requests.leave',
          label: { vi: 'Nghỉ phép', en: 'Leave' },
          icon: 'calendar-days',
          path: '/requests/leave',
          permission: 'requests.request.create',
          order: 4,
        },
        {
          id: 'requests.payments',
          label: { vi: 'Chi tiền & tạm ứng', en: 'Payments & advances' },
          icon: 'wallet',
          path: '/requests/payments',
          permission: 'requests.payment.process',
          order: 5,
        },
        {
          id: 'requests.purchases',
          label: { vi: 'Mua sắm', en: 'Purchases' },
          icon: 'shopping-cart',
          path: '/requests/purchases',
          permission: 'requests.purchase.process',
          order: 6,
        },
        {
          id: 'requests.types',
          label: { vi: 'Loại đề xuất', en: 'Request types' },
          icon: 'settings',
          path: '/requests/types',
          permission: 'requests.type.manage',
          order: 7,
        },
      ],
    },
  ],
  notifications: [
    {
      key: 'requests.pending',
      name: { vi: 'Đề xuất cần tôi duyệt', en: 'Request awaiting my approval' },
      defaultChannels: ['in_app', 'email'],
    },
    {
      key: 'requests.decided',
      name: { vi: 'Kết quả duyệt đề xuất của tôi', en: 'Decision on my request' },
      defaultChannels: ['in_app', 'email'],
    },
    {
      key: 'requests.reminder',
      name: { vi: 'Nhắc duyệt đề xuất', en: 'Approval reminder' },
      description: { vi: 'Khi một đề xuất chờ bạn duyệt quá số giờ trong Cài đặt (mặc định 24).' },
      defaultChannels: ['in_app', 'email'],
    },
    {
      key: 'requests.payable',
      name: { vi: 'Đề nghị chờ chi', en: 'Request awaiting payment' },
      description: { vi: 'Kế toán: đề nghị thanh toán / tạm ứng đã duyệt, cần chi tiền.' },
      defaultChannels: ['in_app', 'email'],
    },
    {
      key: 'requests.paid',
      name: { vi: 'Đề nghị của tôi đã được chi', en: 'My request was paid' },
      defaultChannels: ['in_app', 'email'],
    },
    {
      key: 'requests.purchase-assigned',
      name: { vi: 'Đề xuất mua sắm cần mua', en: 'Purchase to make' },
      description: { vi: 'Hành chính: đề xuất mua sắm vừa được duyệt.' },
      defaultChannels: ['in_app', 'email'],
    },
    {
      key: 'requests.purchase-updated',
      name: {
        vi: 'Đề xuất mua sắm của tôi đã giao / huỷ',
        en: 'My purchase delivered or cancelled',
      },
      defaultChannels: ['in_app', 'email'],
    },
    {
      key: 'requests.advance-overdue',
      name: { vi: 'Tạm ứng quá hạn hoàn ứng', en: 'Overdue advance' },
      description: { vi: 'Gửi người tạm ứng và kế toán; nhắc lại mỗi 7 ngày.' },
      defaultChannels: ['in_app', 'email'],
    },
  ],
  roleTemplates: [
    {
      key: 'director',
      name: { vi: 'Giám đốc (đề xuất)', en: 'Director (requests)' },
      description: { vi: 'Duyệt bước "Giám đốc" và xem mọi đề xuất trong công ty.' },
      grants: [
        { permission: 'requests.approve.director' },
        { permission: 'requests.request.view', scope: 'tenant' },
      ],
    },
    {
      key: 'accounting',
      name: { vi: 'Kế toán (đề xuất)', en: 'Accounting (requests)' },
      description: { vi: 'Duyệt bước "Kế toán", chi tiền và theo dõi tạm ứng.' },
      grants: [
        { permission: 'requests.approve.accounting' },
        { permission: 'requests.payment.process' },
      ],
    },
    {
      key: 'hr',
      name: { vi: 'Nhân sự (đề xuất)', en: 'HR (requests)' },
      description: { vi: 'Duyệt bước "Nhân sự", quản lý quỹ phép và ngày lễ.' },
      grants: [{ permission: 'requests.approve.hr' }, { permission: 'requests.leave.manage' }],
    },
    {
      key: 'office',
      name: { vi: 'Hành chính (đề xuất)', en: 'Office administration (requests)' },
      description: { vi: 'Duyệt bước "Hành chính", mua sắm theo đề xuất đã duyệt.' },
      grants: [
        { permission: 'requests.approve.office' },
        { permission: 'requests.purchase.process' },
      ],
    },
    {
      key: 'unit-head',
      name: { vi: 'Xem đề xuất của đơn vị', en: "View the unit's requests" },
      description: { vi: 'Trưởng đơn vị xem đề xuất của đơn vị mình và các đơn vị con.' },
      grants: [{ permission: 'requests.request.view', scope: 'department_tree' }],
    },
  ],
  events: { publishes: ['requests.leave-approved'], subscribes: [] },
  widgets: [
    {
      key: 'requests.advances',
      title: { vi: 'Tạm ứng chưa hoàn', en: 'Advances to settle' },
      permission: 'requests.request.create',
      order: 7,
    },
    {
      key: 'requests.leave',
      title: { vi: 'Quỹ phép của tôi', en: 'My leave balance' },
      permission: 'requests.request.create',
      order: 6,
    },
    {
      key: 'requests.inbox',
      title: { vi: 'Đề xuất chờ tôi duyệt', en: 'Requests awaiting my approval' },
      permission: 'requests.request.create',
      order: 5,
    },
  ],
});
